import { useMemo } from 'react';
import styled, { useTheme, css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Card } from '@hivebrite/design-system/Card';
import { Divider } from '@hivebrite/design-system/Divider';
import { Dotdotdot } from '@hivebrite/design-system/Dotdotdot';
import {
  FontAwesomeIcon,
  CATEGORIES,
} from '@hivebrite/design-system/FontAwesomeIcon';
import { Image } from '@hivebrite/design-system/Image';
import { Skeleton } from '@hivebrite/design-system/Skeleton';
import {
  linkControlledHoverEffect,
  rawLinkEffect,
  useBreakpoint,
} from '@hivebrite/design-system/Theme';

import {
  prepareDisplayedSections,
  constructSubline,
} from 'hb-react/frontoffice/apps/Jobs/utils/templateUtils';
import JobDropdown from 'hb-react/frontoffice/apps/JobsV2/components/JobDropdown';
import { Link } from 'hb-react/frontoffice/components/Link';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import IconPin from 'hb-react/shared/icons/Pin';
import dayjs from 'hb-react/shared/utils/dayjs/dayjs';
import decodeString from 'hb-react/shared/utils/decodeString';
import onEnterOrSpacePress from 'hb-react/shared/utils/onEnterOrSpacePress';
import { completeName } from 'helpers/name';

export const CardJobLoader = () => (
  <Card height={{ _: 280, m: 260, l: 260, xl: 280 }}>
    <Box mb={3}>
      <Skeleton height={72} width={72} mr={5} />
      <Box display="block" width="100%" css="flex: 1 1 0%;">
        <Skeleton height={24} mb={1} width="100%" />
        <Skeleton height={16} mb={1} width="40%" />
        <Skeleton height={16} width="30%" />
      </Box>
    </Box>
    <Skeleton height={16} mb={1} width="50%" />
    <Skeleton height={16} mb={1} width="50%" />
    <Skeleton height={16} mb={1} width="50%" />
    <Skeleton height={16} width="40%" />

    <Divider mb={3} />

    <Box alignItems="center">
      <Box display="block" width="100%" css="flex: 1 1 0%;">
        <Skeleton height={16} mb={1} width="60%" />
        <Skeleton height={16} width="60%" />
      </Box>
      <Skeleton height={48} width={48} ml={5} css="border-radius: 50%;" />
    </Box>
  </Card>
);

const CardAvatar = ({ item }) => {
  const { flagEvUserPicturesToGlobalStorage } = useFeatureFlag();

  if (item.user || !item.external_provider) {
    const userAvatarUrl = item.user
      ? flagEvUserPicturesToGlobalStorage
        ? item.user.new_photo?.thumbnail_url
        : item.user.photo_thumb_url
      : item.parent_avatar;

    return (
      <Box ml={3}>
        <Image
          css={css`
            border-radius: 50%;
          `}
          src={userAvatarUrl}
          width={48}
          height={48}
          alt={
            item.user
              ? `Profile picture of ${item.user.name}`
              : 'Network avatar'
          }
        />
      </Box>
    );
  }

  return null;
};

const CardLogo = ({ logo }) => {
  const breakpoint = useBreakpoint();
  const theme = useTheme();

  const size = useMemo(() => {
    if (breakpoint === 'xl') {
      return 72;
    }

    if (breakpoint === 'm') {
      return 64;
    }

    return 48;
  }, [breakpoint]);

  const borderRadiusCss = css`
    border-radius: ${theme.themeVariables.globalBorderRadius}px;
  `;

  return (
    <Box flex="0 0 auto" height={size} mr={3} width={size}>
      {logo ? (
        <Image src={logo} css={borderRadiusCss} alt="" />
      ) : (
        <Box
          alignItems="center"
          color="white"
          css={css`
            border: 1px solid ${theme.colors.horizon};
            ${borderRadiusCss}
          `}
          justifyContent="center"
          height="100%"
          width="100%"
        >
          <FontAwesomeIcon
            color={theme.colors.primary}
            height="36px"
            iconName="briefcase"
            iconType={CATEGORIES.DUOTONE}
          />
        </Box>
      )}
    </Box>
  );
};

const CardSubtitle = ({ item, jobKindConfig, locale, onCompanyPage }) => {
  const label = jobKindConfig.find((config) => config.kind_id === item.kind_id)
    ?.names[locale];

  if (onCompanyPage) {
    return <Box>{constructSubline(item)}</Box>;
  }

  return (
    <>
      <Box font="metadata-semibold-asphalt" mb={1}>
        <Dotdotdot clamp={1}>{decodeString(item.company_name)}</Dotdotdot>
      </Box>
      <Box font="metadata-regular-asphalt">{label}</Box>
    </>
  );
};

const CardJob = ({
  currentNetwork,
  currentUser,
  item,
  jobKindConfig,
  onClick,
  onDeleteItem,
  to,
  urls,
  onCompanyPage,
}) => {
  const breakpoint = useBreakpoint();
  const jobSections = prepareDisplayedSections(currentNetwork, item);

  const locale = currentUser?.locale || I18n.locale;

  return (
    <Card
      css={css`
        display: flex;
        flex-direction: column;
        height: 100%;
      `}
    >
      <Box>
        <HeaderStyled
          {...(to && {
            as: Link,
            to,
          })}
          css={css`
            cursor: ${onClick ? 'pointer' : 'default'};
          `}
          onClick={onClick}
          onKeyDown={onEnterOrSpacePress(onClick)}
          tabIndex={onClick || to ? 0 : -1}
        >
          <CardLogo logo={item.logo_thumb_url} />
          <Box
            flexDirection="column"
            flex="1 1 100%"
            minHeight={{ _: 48, m: 60, l: 48, xl: 72 }}
            justifyContent="center"
          >
            <Dotdotdot clamp={2}>
              <HeaderTitleStyled>{decodeString(item.title)}</HeaderTitleStyled>
            </Dotdotdot>
            {breakpoint === 'xl' && (
              <CardSubtitle
                item={item}
                jobKindConfig={jobKindConfig}
                locale={locale}
                mt={1}
                onCompanyPage={onCompanyPage}
              />
            )}
          </Box>
        </HeaderStyled>

        {item.pinned_on_index_page && (
          <Box ml={3} color="primary">
            <IconPin height={30} width={30} />
          </Box>
        )}

        <JobDropdown
          currentNetwork={currentNetwork}
          currentUser={currentUser}
          jobId={item.id}
          jobUserId={item.user?.id}
          onDeleteItem={onDeleteItem}
          editUrl={urls.editItem({ opportunityId: item.id })}
          onCompanyPage={onCompanyPage}
        />
      </Box>

      {breakpoint !== 'xl' && (
        <Box display="block" mb={4}>
          <CardSubtitle
            item={item}
            jobKindConfig={jobKindConfig}
            locale={locale}
            onCompanyPage={onCompanyPage}
          />
        </Box>
      )}

      {onCompanyPage ? (
        <Box display="block">
          <Dotdotdot clamp={3}>{item.stripped_description}</Dotdotdot>
        </Box>
      ) : (
        <Box display="block">
          {jobSections.map((jobSection, index) => (
            <Box key={jobSection.label} mt={index === 0 ? 0 : 2}>
              <Box font="metadata-semibold-asphalt">{jobSection.label}:</Box>
              <Box font="metadata-regular-asphalt" ml={1}>
                {jobSection.value}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {!onCompanyPage && (
        <>
          <Box mt="auto">
            <Divider mt={6} mb={3} />
          </Box>
          <Box flexDirection="column" justifyContent="flex-end">
            <FooterStyled>
              <Box
                {...(currentUser && item.user
                  ? {
                      as: Link,
                      to: Routes.user_path(item.user.id),
                    }
                  : null)}
              >
                <Box flexDirection="column" flex={1} justifyContent="center">
                  {currentUser && (
                    <Box display="block">
                      <Dotdotdot clamp={1}>
                        <Box display="inline" font="metadata-semibold-asphalt">
                          {I18n.t('web.business_opportunities.posted_by')}
                        </Box>
                        <Box
                          display="inline"
                          ml={1}
                          font="metadata-semibold-asphalt"
                        >
                          {item.user ? (
                            <FooterUserStyled>
                              {completeName(item.user)}
                            </FooterUserStyled>
                          ) : item.external_provider === 'multiposting' ? (
                            I18n.t(
                              'web.business_opportunities.card.external_offer',
                            )
                          ) : (
                            item.parent_name
                          )}
                        </Box>
                      </Dotdotdot>
                    </Box>
                  )}
                  <Box font="metadata-regular-asphalt" mt={1}>
                    {dayjs(item.created_at).fromNow()}
                  </Box>
                </Box>
                <CardAvatar item={item} currentNetwork={currentNetwork} />
              </Box>
            </FooterStyled>
          </Box>
        </>
      )}
    </Card>
  );
};

export default CardJob;

const HeaderTitleStyled = styled(Box).attrs({
  font: 'h3-semibold',
  display: 'block',
})`
  overflow-wrap: anywhere;
`;

const HeaderStyled = styled(Box).attrs({
  mb: 3,
  flex: 1,
})`
  &:focus,
  &:hover {
    ${HeaderTitleStyled} {
      ${rawLinkEffect};
    }
  }
`;

const FooterUserStyled = styled.span``;
const FooterStyled = styled(Box).attrs({
  display: 'block',
})`
  ${FooterUserStyled} {
    ${rawLinkEffect};
  }

  &:focus,
  &:hover {
    ${FooterUserStyled} {
      ${linkControlledHoverEffect()};
    }
  }
`;
